import type { PostgrestError } from "@supabase/supabase-js";
import { supa } from "./supa";
import { normalizePersian } from "./persian";
import { startOfDay } from "./time";
import { stateKey } from "./srs";
import { toJson } from "./srs";
import type { Card as FsrsCard } from "ts-fsrs";
import type {
  Card,
  CardNote,
  CardState,
  Collection,
  CollectionColor,
  Direction,
  Enrollment,
  Rating,
  UserSettings,
  Visibility,
} from "./types";

/**
 * Data access over Supabase.
 *
 * Reads deliberately do NOT filter by user — RLS does that, and duplicating the
 * condition here would quietly diverge from the policy. Writes pass `user_id`
 * because the policies check it with `with check`.
 *
 * Every function throws on failure rather than returning an error tuple; screens
 * catch once at the top. Postgres error text is preserved because it's usually
 * the actual answer (e.g. "permission denied for table X" pointing at a missing
 * grant rather than a code bug).
 */

function unwrap<T>(
  res: { data: T | null; error: PostgrestError | null },
  what: string,
): T {
  if (res.error) {
    const hint = res.error.hint ? ` (hint: ${res.error.hint})` : "";
    throw new Error(`${what}: ${res.error.message}${hint}`);
  }
  if (res.data === null) throw new Error(`${what}: no data returned`);
  return res.data;
}

async function requireUid(): Promise<string> {
  const { data } = await supa.auth.getSession();
  const uid = data.session?.user.id;
  if (!uid) throw new Error("not signed in");
  return uid;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export async function loadSettings(): Promise<UserSettings> {
  const res = await supa.from("settings").select("data").maybeSingle();
  if (res.error) throw new Error(`loadSettings: ${res.error.message}`);
  return (res.data?.data as UserSettings | undefined) ?? {};
}

export async function saveSettings(patch: UserSettings): Promise<void> {
  const uid = await requireUid();
  // Read-modify-write is acceptable here and only here: this is a single row of
  // UI preferences, last-write-wins is the desired behaviour, and there is no
  // cross-device correctness at stake the way there is for scheduling.
  const current = await loadSettings();
  const merged = { ...current, ...patch };
  const res = await supa
    .from("settings")
    .upsert({ user_id: uid, data: merged, updated_at: new Date().toISOString() });
  if (res.error) throw new Error(`saveSettings: ${res.error.message}`);
}

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

const COLLECTION_COLS =
  "id, parent_id, root_id, owner_id, title, title_fa, description, lang_front, lang_back, visibility, share_token, is_official, position, has_spoilers, card_count, color, icon, created_at, updated_at";

/** Curated content, top level only. Readable signed out. */
export async function listFeatured(): Promise<Collection[]> {
  return unwrap(
    await supa
      .from("collections")
      .select(COLLECTION_COLS)
      .is("parent_id", null)
      .eq("is_official", true)
      .order("position")
      .order("title"),
    "listFeatured",
  ) as Collection[];
}

/** Collections the signed-in user owns, top level only. */
export async function listMine(): Promise<Collection[]> {
  const uid = await requireUid();
  return unwrap(
    await supa
      .from("collections")
      .select(COLLECTION_COLS)
      .is("parent_id", null)
      .eq("owner_id", uid)
      .order("updated_at", { ascending: false }),
    "listMine",
  ) as Collection[];
}

export interface EnrolledCollection {
  collection: Collection;
  enrollment: Enrollment;
}

/** What the user has added to their study list — the home screen's content. */
export async function listEnrolled(): Promise<EnrolledCollection[]> {
  const enrollments = unwrap(
    await supa.from("enrollments").select("*").order("added_at", { ascending: false }),
    "listEnrolled",
  ) as Enrollment[];
  if (!enrollments.length) return [];

  const collections = unwrap(
    await supa
      .from("collections")
      .select(COLLECTION_COLS)
      .in("id", enrollments.map((e) => e.collection_id)),
    "listEnrolled/collections",
  ) as Collection[];

  const byId = new Map(collections.map((c) => [c.id, c]));
  return enrollments
    .map((enrollment) => {
      const collection = byId.get(enrollment.collection_id);
      return collection ? { collection, enrollment } : null;
    })
    .filter((x): x is EnrolledCollection => x !== null);
}

/**
 * Ids the user has enrolled in, for rendering Add vs Added.
 * One cheap query, so screens don't need a lookup per row. Returns empty when
 * signed out rather than throwing — browsing without an account is supported.
 */
export async function listEnrolledIds(): Promise<Set<string>> {
  const res = await supa.from("enrollments").select("collection_id");
  if (res.error) return new Set();
  return new Set((res.data ?? []).map((r) => (r as { collection_id: string }).collection_id));
}

export interface CollectionTree {
  root: Collection;
  /** Child decks, ordered. Empty when cards hang directly off the root. */
  decks: Collection[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A node plus its direct children, in one query.
 *
 * Works for ANY node, not just a root — the UI links to `/c/<id>` for decks as
 * well as collections. Filtering on `root_id = id` would be wrong for a deck,
 * whose `root_id` points at its parent, so that query matches nothing and every
 * deck link 404s. `or(id.eq, parent_id.eq)` gets both levels in one round trip.
 *
 * `root_id` remains the right key for the enrollment grant and the visibility
 * cascade — it's just not the key for "this node and its children".
 */
export async function getTree(id: string): Promise<CollectionTree> {
  // `.or()` takes a raw PostgREST filter string, so an unvalidated id here would
  // be filter injection. The value comes straight from the URL hash.
  if (!UUID_RE.test(id)) throw new Error("getTree: invalid collection id");

  const rows = unwrap(
    await supa
      .from("collections")
      .select(COLLECTION_COLS)
      .or(`id.eq.${id},parent_id.eq.${id}`)
      .order("position")
      .order("created_at"),
    "getTree",
  ) as Collection[];

  const root = rows.find((r) => r.id === id);
  if (!root) throw new Error("getTree: collection not found or not readable");
  return { root, decks: rows.filter((r) => r.id !== id) };
}

/** A single row, for the add/edit form to prefill from. */
export async function getCollection(id: string): Promise<Collection> {
  if (!UUID_RE.test(id)) throw new Error("getCollection: invalid collection id");
  const res = await supa.from("collections").select(COLLECTION_COLS).eq("id", id).single();
  return unwrap(res, "getCollection") as Collection;
}

export async function createCollection(input: {
  title: string;
  titleFa?: string | null;
  description?: string | null;
  parentId?: string | null;
  position?: number;
  hasSpoilers?: boolean;
  color?: CollectionColor | null;
  icon?: string | null;
}): Promise<Collection> {
  const uid = await requireUid();
  const res = await supa
    .from("collections")
    .insert({
      owner_id: uid,
      parent_id: input.parentId ?? null,
      title: input.title.trim(),
      title_fa: input.titleFa ? normalizePersian(input.titleFa) : null,
      description: input.description ?? null,
      position: input.position ?? 0,
      has_spoilers: input.hasSpoilers ?? false,
      color: input.color ?? null,
      icon: input.icon ?? null,
      // visibility/is_official deliberately omitted: children inherit from the
      // parent by trigger, and roots default to 'private'.
    })
    .select(COLLECTION_COLS)
    .single();
  return unwrap(res, "createCollection") as Collection;
}

export async function updateCollection(
  id: string,
  patch: Partial<
    Pick<Collection, "title" | "title_fa" | "description" | "position" | "has_spoilers" | "visibility" | "color" | "icon">
  >,
): Promise<void> {
  const clean = { ...patch, updated_at: new Date().toISOString() };
  if (clean.title_fa) clean.title_fa = normalizePersian(clean.title_fa);
  const res = await supa.from("collections").update(clean).eq("id", id);
  if (res.error) throw new Error(`updateCollection: ${res.error.message}`);
}

/** Cards, child decks, states, reviews and enrollments all cascade. */
export async function deleteCollection(id: string): Promise<void> {
  const res = await supa.from("collections").delete().eq("id", id);
  if (res.error) throw new Error(`deleteCollection: ${res.error.message}`);
}

/** Setting this on a root cascades to every deck beneath it, by trigger. */
export async function setVisibility(rootId: string, visibility: Visibility): Promise<void> {
  const res = await supa.from("collections").update({ visibility }).eq("id", rootId);
  if (res.error) throw new Error(`setVisibility: ${res.error.message}`);
}

export async function enroll(
  collectionId: string,
  opts?: { newPerDay?: number; directions?: Direction[] },
): Promise<void> {
  const uid = await requireUid();
  const res = await supa.from("enrollments").upsert({
    user_id: uid,
    collection_id: collectionId,
    new_per_day: opts?.newPerDay ?? 10,
    directions: opts?.directions ?? [0],
  });
  if (res.error) throw new Error(`enroll: ${res.error.message}`);
}

export async function unenroll(collectionId: string): Promise<void> {
  const uid = await requireUid();
  const res = await supa
    .from("enrollments")
    .delete()
    .eq("user_id", uid)
    .eq("collection_id", collectionId);
  if (res.error) throw new Error(`unenroll: ${res.error.message}`);
}

export async function updateEnrollment(
  collectionId: string,
  patch: { newPerDay?: number; directions?: Direction[] },
): Promise<void> {
  const uid = await requireUid();
  const row: Record<string, unknown> = {};
  if (patch.newPerDay !== undefined) row.new_per_day = patch.newPerDay;
  if (patch.directions !== undefined) row.directions = patch.directions;
  const res = await supa
    .from("enrollments")
    .update(row)
    .eq("user_id", uid)
    .eq("collection_id", collectionId);
  if (res.error) throw new Error(`updateEnrollment: ${res.error.message}`);
}

/**
 * A shareable URL. The token IS the capability — there is no RLS policy for
 * visibility='link', so possession of this link is what grants access (via
 * claimShare below). Sharing also has to flip visibility off 'private'.
 */
export function shareLink(collection: Collection): string {
  return `${location.origin}/#/share/${collection.share_token}`;
}

/** Trade a share token for an enrollment, which is what grants the read. */
export async function claimShare(token: string): Promise<string> {
  const res = await supa.rpc("claim_shared_collection", { p_token: token });
  if (res.error) throw new Error(`claimShare: ${res.error.message}`);
  return res.data as string;
}

/**
 * Deep-copy a collection to the caller. Editing official or shared content must
 * never mutate what other people are studying, so any edit attempt on a
 * collection you don't own routes through here first.
 */
export async function forkCollection(id: string): Promise<string> {
  const res = await supa.rpc("fork_collection", { p_id: id });
  if (res.error) throw new Error(`forkCollection: ${res.error.message}`);
  return res.data as string;
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

const CARD_COLS =
  "id, collection_id, front, back, back_general, kind, ipa, audio_url, example, example_back, notes, tags, position, data, created_at, updated_at";

export async function listCards(collectionIds: string[]): Promise<Card[]> {
  if (!collectionIds.length) return [];
  return unwrap(
    await supa
      .from("cards")
      .select(CARD_COLS)
      .in("collection_id", collectionIds)
      .order("position")
      .order("created_at"),
    "listCards",
  ) as Card[];
}

export interface CardInput {
  front: string;
  back: string;
  /** Everyday meaning, when the deck's sense is narrower. */
  backGeneral?: string | null;
  kind?: Card["kind"];
  ipa?: string | null;
  audioUrl?: string | null;
  example?: string | null;
  exampleBack?: string | null;
  notes?: string | null;
  tags?: string[];
  position?: number;
}

/** Persian is normalized on the way in so search and dedup can't silently miss. */
function cardRow(collectionId: string, input: CardInput, index = 0) {
  return {
    collection_id: collectionId,
    front: input.front.trim(),
    back: normalizePersian(input.back),
    back_general: input.backGeneral ? normalizePersian(input.backGeneral) : null,
    kind: input.kind ?? (input.front.trim().includes(" ") ? "phrase" : "word"),
    ipa: input.ipa?.trim() || null,
    audio_url: input.audioUrl ?? null,
    example: input.example?.trim() || null,
    example_back: input.exampleBack ? normalizePersian(input.exampleBack) : null,
    notes: input.notes?.trim() || null,
    tags: input.tags ?? [],
    position: input.position ?? index,
  };
}

export async function createCard(collectionId: string, input: CardInput): Promise<Card> {
  return unwrap(
    await supa.from("cards").insert(cardRow(collectionId, input)).select(CARD_COLS).single(),
    "createCard",
  ) as Card;
}

/** Bulk insert for the importer. Chunked to keep request bodies sane. */
export async function createCards(
  collectionId: string,
  inputs: CardInput[],
  chunkSize = 200,
): Promise<Card[]> {
  const created: Card[] = [];
  for (let i = 0; i < inputs.length; i += chunkSize) {
    const chunk = inputs.slice(i, i + chunkSize).map((input, j) => cardRow(collectionId, input, i + j));
    const rows = unwrap(
      await supa.from("cards").insert(chunk).select(CARD_COLS),
      `createCards[${i}]`,
    ) as Card[];
    created.push(...rows);
  }
  return created;
}

export async function updateCard(id: string, patch: Partial<CardInput>): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.front !== undefined) row.front = patch.front.trim();
  if (patch.back !== undefined) row.back = normalizePersian(patch.back);
  if (patch.backGeneral !== undefined)
    row.back_general = patch.backGeneral ? normalizePersian(patch.backGeneral) : null;
  if (patch.kind !== undefined) row.kind = patch.kind;
  if (patch.ipa !== undefined) row.ipa = patch.ipa?.trim() || null;
  if (patch.audioUrl !== undefined) row.audio_url = patch.audioUrl;
  if (patch.example !== undefined) row.example = patch.example?.trim() || null;
  if (patch.exampleBack !== undefined)
    row.example_back = patch.exampleBack ? normalizePersian(patch.exampleBack) : null;
  if (patch.notes !== undefined) row.notes = patch.notes?.trim() || null;
  if (patch.tags !== undefined) row.tags = patch.tags;
  if (patch.position !== undefined) row.position = patch.position;

  const res = await supa.from("cards").update(row).eq("id", id);
  if (res.error) throw new Error(`updateCard: ${res.error.message}`);
}

export async function deleteCard(id: string): Promise<void> {
  const res = await supa.from("cards").delete().eq("id", id);
  if (res.error) throw new Error(`deleteCard: ${res.error.message}`);
}

// ---------------------------------------------------------------------------
// Personal notes
// ---------------------------------------------------------------------------

/** A learner's own note on a card, or "" when they haven't written one. */
export async function loadCardNote(cardId: string): Promise<string> {
  const res = await supa.from("card_notes").select("note").eq("card_id", cardId).maybeSingle();
  if (res.error) throw new Error(`loadCardNote: ${res.error.message}`);
  return (res.data as { note: string } | null)?.note ?? "";
}

/** Notes for several cards at once, keyed by card id — for list views. */
export async function loadCardNotes(cardIds: string[]): Promise<Map<string, string>> {
  if (!cardIds.length) return new Map();
  const res = await supa.from("card_notes").select("card_id, note").in("card_id", cardIds);
  if (res.error) return new Map(); // notes are an enhancement; never block a list on them
  return new Map((res.data ?? []).map((r) => [(r as CardNote).card_id, (r as CardNote).note]));
}

/** Empty text deletes the row rather than storing a blank one. */
export async function saveCardNote(cardId: string, note: string): Promise<void> {
  const uid = await requireUid();
  const text = note.trim();

  if (!text) {
    const res = await supa.from("card_notes").delete().eq("user_id", uid).eq("card_id", cardId);
    if (res.error) throw new Error(`saveCardNote: ${res.error.message}`);
    return;
  }

  const res = await supa.from("card_notes").upsert({
    user_id: uid,
    card_id: cardId,
    note: text,
    updated_at: new Date().toISOString(),
  });
  if (res.error) throw new Error(`saveCardNote: ${res.error.message}`);
}

// ---------------------------------------------------------------------------
// Study
// ---------------------------------------------------------------------------

export interface StudySet {
  tree: CollectionTree;
  cards: Card[];
  /** Keyed `${card_id}:${direction}` — the shape buildQueue expects. */
  states: Map<string, CardState>;
  enrollment: Pick<Enrollment, "new_per_day" | "directions" | "added_at">;
  /** Cards first introduced today, in the user's timezone. */
  introducedToday: number;
}

/**
 * Everything needed to build a study queue, in four round trips.
 * `buildQueue()` in srs.ts turns this into an ordered list; it's kept pure so it
 * can be tested without a database.
 */
export async function loadStudySet(rootId: string, tz: string): Promise<StudySet> {
  const tree = await getTree(rootId);
  const collectionIds = [tree.root.id, ...tree.decks.map((d) => d.id)];
  const cards = await listCards(collectionIds);

  const cardIds = cards.map((c) => c.id);
  const states = new Map<string, CardState>();
  if (cardIds.length) {
    const rows = unwrap(
      await supa.from("card_states").select("*").in("card_id", cardIds),
      "loadStudySet/states",
    ) as CardState[];
    for (const row of rows) states.set(stateKey(row.card_id, row.direction), row);
  }

  // Enrollment lives on the ROOT, so studying a single deck must look it up by
  // root_id rather than by the node we were handed — otherwise a deck-scoped
  // session silently falls back to default limits instead of the user's.
  const enrollment = await getEnrollment(tree.root.root_id);
  const introducedToday = await countIntroducedToday(cardIds, tz);

  return { tree, cards, states, enrollment, introducedToday };
}

async function getEnrollment(
  collectionId: string,
): Promise<Pick<Enrollment, "new_per_day" | "directions" | "added_at">> {
  const res = await supa
    .from("enrollments")
    .select("new_per_day, directions, added_at")
    .eq("collection_id", collectionId)
    .maybeSingle();
  if (res.error) throw new Error(`getEnrollment: ${res.error.message}`);
  // Not enrolled (e.g. previewing a featured deck) — fall back to the defaults.
  // `added_at: now` is a harmless placeholder here: everything reads as fresh/
  // unstarted for a visitor who was never actually enrolled, so "started today"
  // cannot mislead.
  return (res.data as Pick<Enrollment, "new_per_day" | "directions" | "added_at"> | null) ?? {
    new_per_day: 10,
    directions: [0],
    added_at: new Date().toISOString(),
  };
}

/**
 * How many cards were seen for the FIRST time today, scoped to this collection.
 *
 * `state_before is null` identifies a first-ever review exactly — which is why
 * submit_review records it. Rather than filtering server-side by a potentially
 * huge `card_id` list (which would blow up the URL on a 1000-card deck), fetch
 * today's new-card reviews for the user — at most a few dozen rows, since it's
 * bounded by the daily cap — and intersect in memory.
 */
export async function countIntroducedToday(cardIds: string[], tz: string): Promise<number> {
  if (!cardIds.length) return 0;
  const since = startOfDay(tz).toISOString();
  const rows = unwrap(
    await supa
      .from("reviews")
      .select("card_id")
      .is("state_before", null)
      .gte("reviewed_at", since),
    "countIntroducedToday",
  ) as { card_id: string }[];

  const inSet = new Set(cardIds);
  const seen = new Set<string>();
  for (const row of rows) if (inSet.has(row.card_id)) seen.add(row.card_id);
  return seen.size;
}

/**
 * Lifetime review count, for the home-page stats card.
 *
 * `head: true` — no rows actually transfer, just the count Postgres already
 * tracks internally, so this stays cheap no matter how long someone's been
 * studying.
 */
export async function totalReviewCount(): Promise<number> {
  const res = await supa.from("reviews").select("id", { count: "exact", head: true });
  if (res.error) throw new Error(`totalReviewCount: ${res.error.message}`);
  return res.count ?? 0;
}

/**
 * Consecutive days with at least one review, ending today or yesterday — a
 * streak survives until the day is actually over, so not having studied YET
 * today doesn't break it.
 *
 * Computed client-side from a bounded recent window rather than a day-by-day
 * loop of queries: 60 days comfortably covers any real streak, and grouping
 * by the learner's own day boundary (not UTC) is exactly what `startOfDay`
 * already exists for elsewhere in this file.
 */
export async function studyStreak(tz: string): Promise<number> {
  const since = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
  const rows = unwrap(
    await supa.from("reviews").select("reviewed_at").gte("reviewed_at", since),
    "studyStreak",
  ) as { reviewed_at: string }[];

  const days = new Set(rows.map((r) => startOfDay(tz, new Date(r.reviewed_at)).getTime()));
  const today = startOfDay(tz).getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  let cursor = days.has(today) ? today : today - oneDay;
  let streak = 0;
  while (days.has(cursor)) {
    streak++;
    cursor -= oneDay;
  }
  return streak;
}

/**
 * Persist a graded review. Atomic server-side: appends the log row and upserts
 * scheduling state in one transaction, so two devices studying at once can never
 * half-write.
 */
export async function submitReview(
  cardId: string,
  direction: Direction,
  rating: Rating,
  card: FsrsCard,
  ms?: number,
): Promise<CardState> {
  const res = await supa.rpc("submit_review", {
    p_card: cardId,
    p_direction: direction,
    p_rating: rating,
    p_state: toJson(card),
    p_ms: ms ?? null,
  });
  if (res.error) throw new Error(`submitReview: ${res.error.message}`);
  const row = (Array.isArray(res.data) ? res.data[0] : res.data) as CardState;
  if (!row) throw new Error("submitReview: no state returned");
  return row;
}

/** Undo the last review of a card. `card` is the ts-fsrs rollback() result. */
export async function undoReview(
  cardId: string,
  direction: Direction,
  card: FsrsCard,
): Promise<CardState> {
  const res = await supa.rpc("undo_review", {
    p_card: cardId,
    p_direction: direction,
    p_state: toJson(card),
  });
  if (res.error) throw new Error(`undoReview: ${res.error.message}`);
  const row = (Array.isArray(res.data) ? res.data[0] : res.data) as CardState;
  if (!row) throw new Error("undoReview: no state returned");
  return row;
}

/**
 * "I already know this" — retire a card without studying it.
 *
 * Deliberately does NOT write a review row. This isn't the result of a memory
 * test, and recording it as a successful recall would feed a data point the
 * learner never actually produced into the FSRS history that a future personal
 * optimizer trains on. It also means the card doesn't consume the daily new-card
 * budget, since that is counted from reviews with `state_before is null`.
 *
 * Scoped to ONE direction on purpose: knowing that "sheriff" means کلانتر does
 * not mean you can produce "sheriff" from کلانتر. If production is enabled later,
 * the card should legitimately come back in that direction.
 */
export async function suspendCard(cardId: string, direction: Direction): Promise<void> {
  const uid = await requireUid();
  // Upsert rather than update: a card the user has never seen has no state row at
  // all, which is exactly the case this feature exists for. Remaining columns
  // take their schema defaults.
  const res = await supa
    .from("card_states")
    .upsert({ user_id: uid, card_id: cardId, direction, suspended: true });
  if (res.error) throw new Error(`suspendCard: ${res.error.message}`);
}

/** Reverse suspendCard, for undo. */
export async function unsuspendCard(cardId: string, direction: Direction): Promise<void> {
  const uid = await requireUid();
  const res = await supa
    .from("card_states")
    .update({ suspended: false })
    .eq("user_id", uid)
    .eq("card_id", cardId)
    .eq("direction", direction);
  if (res.error) throw new Error(`unsuspendCard: ${res.error.message}`);
}

export interface DueSummary {
  due: number;
  fresh: number;
  total: number;
  /**
   * Cards never studied in any active direction, UNCLAMPED by the daily new-
   * card budget — unlike `fresh`, which is deliberately capped to "how many
   * you can still start today". A progress bar needs the true count: a deck
   * you've been through for weeks should read as nearly finished even in a
   * session where the budget has already been used up.
   */
  notStarted: number;
  /** When the caller enrolled in this collection — `enrollments.added_at`. */
  addedAt: string;
}

/**
 * Counts for a collection card, without building the full queue.
 * Reuses loadStudySet so the numbers can never disagree with what a session
 * actually serves — a cheaper dedicated query would be a second source of truth.
 */
export async function dueSummary(rootId: string, tz: string): Promise<DueSummary> {
  const set = await loadStudySet(rootId, tz);
  const now = Date.now();
  const directions = set.enrollment.directions.length ? set.enrollment.directions : [0 as Direction];

  let due = 0;
  let fresh = 0;
  const counted = new Set<string>();

  for (const card of set.cards) {
    for (const direction of directions) {
      if (counted.has(card.id)) continue; // match buildQueue's sibling burying
      const state = set.states.get(stateKey(card.id, direction));
      if (state?.suspended) continue;
      if (!state) {
        fresh++;
        counted.add(card.id);
      } else if (new Date(state.due).getTime() <= now) {
        due++;
        counted.add(card.id);
      }
    }
  }

  const budget = Math.max(0, set.enrollment.new_per_day - set.introducedToday);
  return {
    due,
    fresh: Math.min(fresh, budget),
    total: set.cards.length,
    notStarted: fresh,
    addedAt: set.enrollment.added_at,
  };
}
